import { useTranslation } from 'react-i18next';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';
import type {
  ChargePointData,
  ChargePointGroupData,
} from '../../types/charge-point.types';

interface AvailabilitySectionProps {
  chargePoint?: ChargePointData;
  chargePointGroup?: ChargePointGroupData;
  groupMembers?: ChargePointData[];
  isLoadingGroup?: boolean;
}

/**
 * Display availability information
 * - For single charge points: Shows status (Available/Busy/Passive)
 * - For groups: Shows available count / total count
 */
export function AvailabilitySection({
  chargePoint,
  chargePointGroup,
  groupMembers = [],
  isLoadingGroup = false,
}: AvailabilitySectionProps) {
  const { t } = useTranslation();
  const uiMode = useUiModeStore((s) => s.uiMode);

  // Group availability
  if (chargePointGroup) {
    const totalCount =
      groupMembers.length ||
      chargePointGroup.chargePointCount ||
      chargePointGroup.availableCount ||
      0;
    const availableCount = chargePointGroup.availableCount || 0;

    return (
      <div className="flex items-center gap-2 p-3 bg-ink/[0.03] rounded-lg">
        {isLoadingGroup ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ink"></div>
            <span className="text-sm text-ink/80">
              {t('chargePoint.loadingAvailability')}
            </span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <span className="text-2xl font-bold text-ink">
                {availableCount}
              </span>
              <span className="text-sm text-muted">/ {totalCount}</span>
            </div>
            <span className="text-sm text-ink/80">
              {uiMode === 'operator'
                ? t('chargePoint.operatorMode.availabilityTitle')
                : t('common.status.available')}
            </span>
          </>
        )}
      </div>
    );
  }

  // Single charge point availability
  if (chargePoint) {
    const state = chargePoint.chargePointState;

    let statusColor = 'text-muted';
    let statusBg = 'bg-ink/5';
    let statusText = t('common.status.unknown');
    let dotColor = 'bg-ink/25';

    switch (state) {
      case 'available':
        statusColor = 'text-ok';
        statusBg = 'bg-ok/10';
        statusText = t('common.status.available');
        dotColor = 'bg-green-600';
        break;
      case 'busy':
        // Busy charge points carry a grey pin on the map, so keep the badge grey to match.
        statusColor = 'text-ink/80';
        statusBg = 'bg-ink/5';
        statusText = t('common.status.busy');
        dotColor = 'bg-gray-500';
        break;
      case 'reserved':
        statusColor = 'text-muted';
        statusBg = 'bg-monta-blue/10';
        statusText = t('common.status.reserved');
        dotColor = 'bg-blue-600';
        break;
      case 'error':
        statusColor = 'text-red-700 dark:text-red-300';
        statusBg = 'bg-red-50 dark:bg-red-500/10';
        statusText = t('common.status.error');
        dotColor = 'bg-red-600';
        break;
      case 'disconnected':
        // Disconnected charge points carry a red pin on the map, so keep the badge red to match.
        statusColor = 'text-red-700 dark:text-red-300';
        statusBg = 'bg-red-50 dark:bg-red-500/10';
        statusText = t('common.status.disconnected');
        dotColor = 'bg-red-600';
        break;
      case 'passive':
        statusColor = 'text-ink/80';
        statusBg = 'bg-ink/5';
        statusText = t('common.status.passive');
        dotColor = 'bg-ink/25';
        break;
    }

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${statusBg}`}
      >
        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className={`text-sm font-medium ${statusColor}`}>
          {statusText}
        </span>
      </div>
    );
  }

  return null;
}
